import { useEffect, useState } from 'react'
import { Card, Col, Row, Typography, Button, List, Avatar, Skeleton, Space, Tooltip, Popconfirm, Form, Input, DatePicker, Switch, Popover, message } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'

import { SearchSelect } from './../../../../../component/Button';
import { useEgresadoContext } from './../../../context/EgresadoProvident';

import functions from "./../../../shared/functions";
import useExpLaboral from "./HookExpLaboral";

import dayjs from 'dayjs';


const { Title } = Typography;
const { TextArea } = Input;

const ExperienciaLaboral = ({ id }) => {
  const { alumno } = useEgresadoContext();

  const {
    data,
    newData,
    setNewData,
    editing,
    startEdit,
    cancelEdit,
    saveRecord,
    deleteRecord,
    loading,
    catalogRangoSalarial,
    catEstados
  } = useExpLaboral(alumno?.exp_laboral);



  return (
    <Card id={id}>
      {
        (!editing && !newData) ?
          <ExperienciaLaboralTable
            data={data}
            loading={loading}
            startEdit={startEdit}
            deleteRecord={deleteRecord}
            setNewData={setNewData}
          />
          :
          <FormExperienciaLaboral
            editing={editing}
            onSave={saveRecord}
            onCancel={cancelEdit}
            catalogEstados={catEstados}
            catalogRangoSalarial={catalogRangoSalarial}
          />
      }
    </Card>
  )
}

const ExperienciaLaboralTable = ({ data, loading, startEdit, deleteRecord, setNewData }) => {

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Popover
            styles={{ root: { width: 250 } }}
            title="Experiencia profesional"
            content={
              <div style={{ textAlign: 'justify' }}>
                Puedes agregar empleos, prácticas profesionales, servicio social, formación Dual u otra experiencia laboral relacionada con tu formación.
              </div>
            }
            placement="right" icon={<InfoCircleOutlined />}>

            <Title level={5} strong>Experiencia profesional <QuestionCircleOutlined /></Title>
          </Popover>
        </Col>
        <Col>
          <Button
            type="text"
            style={{ color: '#7c0433' }}
            icon={<PlusOutlined />}
            onClick={() => setNewData(true)}
          >
            Agregar experiencia profesional
          </Button>
        </Col>


        <Col span={24}>
          <List
            key={"experiencia_laboral"}
            className="demo-loadmore-list"
            size="small"
            loading={loading}
            dataSource={data}
            itemLayout="horizontal"
            pagination={{ pageSize: 3 }}
            renderItem={(item, index) => (
              <List.Item
                key={item.id_exp}
                actions={[
                  <Space>
                    <Tooltip title="Editar experiencia laboral">
                      <Button
                        key="edit"
                        icon={<EditOutlined />}
                        shape='circle'
                        size="small"
                        onClick={() => startEdit(item)}
                      />
                    </Tooltip>
                    <Tooltip title="Eliminar experiencia laboral">
                      <Popconfirm
                        title="¿Deseas eliminar esta experiencia laboral?"
                        onConfirm={() => deleteRecord(item)}
                      >
                        <Button
                          key="delete"
                          icon={<DeleteOutlined />}
                          shape='circle'
                          size="small"
                          danger
                        />
                      </Popconfirm>

                    </Tooltip>
                  </Space>
                ]}
              >
                <Skeleton avatar title={false} loading={item.loading} active>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={`https://api.dicebear.com/7.x/initials/svg?seed=${item?.empresa}&scale=75`}
                      />
                    }
                    title={<strong>{item.puesto}</strong>}
                    description={
                      <Row gutter={[0, 4]}>
                        <Col span={24}>
                          {item?.empresa} - {item?.estado?.nombre_estado}
                        </Col>
                        <Col span={24}>
                          {item?.fecha && functions.formatearRangoFechas(item?.fecha)} ({functions.calcularAniosMeses(item?.fecha)})
                        </Col>
                        <Col span={24}>
                          {item?.descripcion_puesto?.length > 200 ? `${item?.descripcion_puesto?.slice(0, 200)} ...` : item?.descripcion_puesto}
                        </Col>
                      </Row>
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </>
  )
}


const FormExperienciaLaboral = ({ editing, onSave, onCancel, catalogEstados, catalogRangoSalarial }) => {
  const [form] = Form.useForm();
  const [isWorking, setIsWorking] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(null); // Para almacenar temporalmente


  useEffect(() => {
    if (editing) {
      form.setFieldsValue(editing);
      setIsWorking(!editing.fecha[1]);
    }
  }, [editing]);

  const handleCalendarChange = (dates) => {
    if (dates && dates[0]) {
      setFechaInicio(dates[0]);
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const response = await onSave(values);

    if (response) {
      form.resetFields();
      onCancel();
    }
  }

  const handleSwitchChange = (checked) => {
    if (checked) {
      const fechaValue = form.getFieldValue('fecha')?.[0] || fechaInicio;
      form.setFieldsValue({
        fecha: [fechaValue, null],
      });
    }
    setIsWorking(checked);
  }

  const checkConflict = (getFieldValue, fieldName) => ({
    validator(_, value) {
      if (value && getFieldValue(fieldName)) {
        return Promise.reject(new Error(`No se puede activar porque "${fieldName === 'servicio_social' ? 'Educación Dual' : 'Servicio Social'}" ya está activo.`));
      }
      return Promise.resolve();
    },
  });

  return (
    <>
      <Row>
        <Col>
          <Title level={5} strong>Agregar experiencia profesional</Title>
        </Col>
        <Col>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            onFinishFailed={() => message.error("Por favor complete todos los campos")}
          >
            <Row gutter={16}>
              <Col span={24} >
                <Form.Item name="empresa" label="Empresa" rules={[{ required: true, min: 3, max: 100, whitespace: true }]}>
                  <Input placeholder="Ingrese el nombre de la empresa" />
                </Form.Item>
              </Col>
              <Col span={24} >
                <Form.Item name="id_estado" label="Ubicación de la empresa" rules={[{ required: true }]}>
                  <SearchSelect dataset={catalogEstados} />
                </Form.Item>
              </Col>
              <Col span={24}  >
                <Form.Item name="puesto" label="Puesto" rules={[{ required: true, min: 3, max: 100, whitespace: true }]}>
                  <Input placeholder="Ingrese el puesto" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="descripcion_puesto" label="Descripción del puesto" rules={[{ required: true, min: 10, max: 500, whitespace: true }]}>
                  <TextArea rows={3} placeholder="Describe tus responsabilidades y logros..." />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="id_rango_salarial" label="Rango de salario mensual" rules={[{ required: true }]}>
                  <SearchSelect dataset={catalogRangoSalarial} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="fecha"
                  label="Periodo laboral"
                  rules={[
                    {
                      required: true,
                      message: "Por favor seleccione el período de estudio",
                    },
                    {
                      validator: (_, value) => {
                        if (!value || !Array.isArray(value)) {
                          return Promise.reject("Seleccione una fecha válida");
                        }

                        const [start, end] = value;

                        // Si está estudiando actualmente, solo start es obligatorio
                        if (isWorking) {
                          if (!start) {
                            return Promise.reject("Debe seleccionar la fecha de inicio");
                          }
                          return Promise.resolve();
                        }

                        // Si no está estudiando, ambas fechas son obligatorias
                        if (!start || !end) {
                          return Promise.reject("Debe seleccionar ambas fechas");
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <DatePicker.RangePicker
                    style={{ width: '80%' }}
                    format={"DD-MM-YYYY"}
                    placeholder={["Fecha de inicio", "Hasta ahora"]}
                    onCalendarChange={handleCalendarChange}
                    allowEmpty={[true, isWorking]}
                    disabled={[false, isWorking]}
                    disabledDate={(current) => {
                      const currentDate = dayjs();
                      const oneMonthLater = currentDate.add(1, 'month');
                      return current && current.isAfter(oneMonthLater); // Deshabilita fechas fuera de este rango
                    }}
                    renderExtraFooter={() => (
                      <>
                        <Typography.Text italic>¿Actualmente continúa con laborando?</Typography.Text>
                        <Switch
                          checkedChildren="Si"
                          unCheckedChildren="No"
                          checked={isWorking}
                          onChange={handleSwitchChange}
                        />
                      </>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="educacion_dual"
                  label="¿Fue Educación Dual?"
                  tooltip="¿Estudiaste bajo el modelo dual? El modelo dual combina estudios teóricos con prácticas en empresas. Indica 'Sí' si formaste parte de este tipo de formación."
                  rules={[
                    ({ getFieldValue }) => checkConflict(getFieldValue, 'servicio_social')
                  ]}
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="servicio_social"
                  label="¿Fue servicio social o prácticas profesionales?"
                  tooltip="¿Realizaste prácticas profesionales como parte de tu formación académica? Marca 'Sí' si participaste en un servicio social o prácticas obligatorias."
                  rules={[
                    ({ getFieldValue }) => checkConflict(getFieldValue, 'educacion_dual')
                  ]}
                >
                  <Switch checkedChildren="Si" unCheckedChildren="No" />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Space >
                <Button type="primary" htmlType="submit">
                  {editing ? "Guardar cambios" : "Agregar"}
                </Button>
                <Button onClick={() => {
                  onCancel();
                  form.resetFields();
                }}>
                  Cancelar
                </Button>
              </Space>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  )

}

export default ExperienciaLaboral;